import path from 'path';
import { mkdir, writeFile, stat } from 'fs/promises';
import { existsSync } from 'fs';
import Debug from '../../helpers/debug.js';
import ora from 'ora';
import axios from 'axios';
import Table from 'cli-table';
import AWS from 'aws-sdk';
import ProgressBar from 'progress';
import descriptions from '../../helpers/descriptions.js';
import print, { pr, pp, cl, themed, printJSON } from '../../helpers/print.js';
import { filterFiles, streamToString } from '../../helpers/utils.js';
import { genCredentialsOnClientApi } from '../../helpers/api.js';
import { getEntityLabel } from '../../helpers/flow.js';

const debug = Debug('commands:etl:download');

const command = 'download';

const desc = 'Download ETL scripts';

const builder = async (yargs) => {
  debug('builder', command);

  return yargs
    .option('flow', descriptions.options['flow'].config)
    .demandOption('flow', descriptions.options['flow'].demandText)
    .option('tap', descriptions.options['tap'].config)
    .demandOption('tap', descriptions.options['tap'].demandText)
    .option('downloadTo', descriptions.options['downloadTo'].config)
    .demandOption('downloadTo', descriptions.options['downloadTo'].demandText)
    .option('tenant', { ...descriptions.options['tenant'].config, default: 'default' })
    .option('overwrite', descriptions.options['overwrite'].config)
    .demandOption('overwrite', descriptions.options['overwrite'].demandText);
};

const handler = async (argv) => {
  debug('handler', command, argv);

  const { hg, json, apikey, env, flow, tap, tenant, downloadTo, overwrite } = argv;
  const { clientApiBaseUri } = hg;

  let message;
  let spinner = ora();
  const folderPrefix = path.resolve(process.cwd(), downloadTo);
  try {
    // 1. get STS credentials
    message = themed(`Verifying user and authorizing`);
    !json && spinner.start(themed(`In progress: ${message}...`, 'secondary'));
    const { accessKeyId, secretAccessKey, sessionToken } = await genCredentialsOnClientApi({
      debug,
      baseUri: clientApiBaseUri,
      task: 'etl-download',
      env,
      tenant,
      flow,
      tap,
      apikey,
    });
    // created authenticated S3 instance
    const s3 = new AWS.S3({
      accessKeyId,
      secretAccessKey,
      sessionToken,
    });
    !json && spinner.succeed(themed(`Finished: ${message}.`, 'secondary'));

    // 2. get list of downloadable files
    message = themed(`Scanning for downloadable files`);
    !json && spinner.start(themed(`In progress: ${message}...`, 'secondary'));
    const keyPrefix = `${tenant}/flows/${flow}/${await getEntityLabel(s3, env, tenant, flow)}/${tap}/etl/`;
    const params = {
      Bucket: env,
      Prefix: keyPrefix,
    };
    const { Contents: s3Files } = await s3.listObjectsV2(params).promise();
    !json && spinner.succeed(themed(`Finished: ${message}.`, 'secondary'));
    debug('s3-list-res', s3Files);
    // filter unwanted files from source
    const filesToDownload = s3Files
      ? filterFiles(
          s3Files.map((item) => item.Key),
          { pattern: '!(.ipynb_checkpoints/*)' }
        )
      : [];
    if (!filesToDownload || filesToDownload.length === 0) {
      json
        ? printJSON({ status: 'success', downloadedFiles: [] })
        : spinner.warn(themed(`Warning: ${themed('Nothing to download!')}`, 'secondary'));
      return;
    }

    // 3. download files
    message = themed(`Downloading script files to ${themed(folderPrefix, 'info')}`);
    !json && spinner.info(themed(`Info: ${message}.`, 'secondary'));
    const table = new Table({ head: ['File', 'Status'] });
    for await (const file of filesToDownload) {
      const subPath = file.substring(keyPrefix.length); // path.basename(`${env}/${item.Key}`);
      const fullPath = path.resolve(folderPrefix, subPath);
      debug('file', fullPath);
      if (file.endsWith('/')) {
        !existsSync(fullPath) && (await mkdir(fullPath, { recursive: true }));
        continue;
      }
      // skip if exists and overwrite is false
      if (!overwrite) {
        try {
          await stat(fullPath);
          debug('exists, skipping');
          table.push([subPath, 'Skipped']);
          continue;
        } catch (err) {
          if (err.code !== 'ENOENT') cl(err);
        }
      }

      message = themed(`Downloading file: ${themed(subPath, 'info')}`);
      !json && spinner.start(themed(`In progress: ${message}...`, 'secondary'));
      // ensure destination folder exists
      await mkdir(path.dirname(fullPath), { recursive: true });
      // download s3 object into destination folder
      const fileStream = s3.getObject({ Bucket: env, Key: file }).createReadStream();
      await writeFile(fullPath, await streamToString(fileStream));
      !json && spinner.succeed(themed(`Finished: ${message}.`, 'secondary'));

      table.push([subPath, 'Downloaded']);
    }

    if (json) {
      printJSON({ status: 'success', downloadedFiles: filesToDownload });
    } else {
      // print results
      cl(table.toString());
    }
  } catch (err) {
    if (json) {
      printJSON({ status: 'error', error: err });
    } else {
      spinner.fail(themed(`Error: ${message}.`, 'secondary'));
      pr(themed(`Message: ${themed(err.message)}`, 'secondary'));
      debug(err);
      if (err && err.response && err.response.data) {
        debug('response', err.response.data);
      }
    }
  }
};

export { command, desc, builder, handler };
